import { useCallback } from 'react'
import { useHistory } from 'react-router'
import { toast } from 'react-toastify'
import isArray from 'lodash/isArray'
import { useTranslation } from 'react-i18next'

import { useMutation } from 'services/query'
import { createEvent, TCreateEventPayload } from 'api/events'
import { TEventInfoFormValues } from 'App/components/Events/EventInfoForm'
import { toastDefaultOptions } from 'globalConstants'
import { parseError } from 'utils'

type TUseCreateEventRequestProps = {
  onSuccess?: () => void
} & Pick<TCreateEventPayload, 'representativeId' | 'representativeType'>

export const useCreateEventRequest = (props?: TUseCreateEventRequestProps) => {
  const { representativeId, representativeType, onSuccess } = props ?? {}

  const { push } = useHistory()
  const { t } = useTranslation()

  const { mutateAsync: createEventMutate, isLoading } = useMutation({
    mutationFn: createEvent
  })

  const handleSubmit = useCallback(
    async ({ location, city, dates, title, type }: TEventInfoFormValues) => {
      const isRangeDates = isArray(dates)
      const startOn = isRangeDates ? dates[0] : dates
      const endOn = isRangeDates ? dates[1] : dates

      if (!startOn || !endOn) {
        return
      }

      const baseBody = {
        title,
        startOn,
        endOn,
        cityId: city ? city.id.toString() : undefined,
        location,
        type
      }

      const eventPayload =
        representativeId && representativeType
          ? { ...baseBody, representativeId, representativeType }
          : baseBody

      try {
        const { id: eventId } = await createEventMutate(eventPayload)

        onSuccess?.()
        push(`/events/${eventId}/highlights/info`)
        toast.success(t('events.createEventSuccessToast'), toastDefaultOptions)
      } catch (error) {
        console.error(error)
        toast.error(parseError(error), toastDefaultOptions)
      }
    },
    [createEventMutate, representativeId, representativeType, onSuccess, push, t]
  )

  return { isLoading, handleSubmit }
}
